import { appContextFields } from './app-context';
import { exceptionFieldsFromError, maskPii } from './exception';
import { toRfc5424Severity } from './rfc5424';
import { activeTraceFields } from './trace';

/**
 * commerce-client 통합 로거 (외부 로깅 의존성 없음 / 추적은 OTel 유지).
 *
 * - 서버(Node): RFC 5424 severity + (OTel) W3C trace context 를 실은 JSON 한 줄을
 *   stdout 으로 출력 → k8s pod 로그 수집기(ELK)가 색인.
 * - 클라이언트(브라우저): error 이상만 `/api/log` 로 transmit → 서버가 재-emit 하여
 *   pod stdout 에 도달. 그 외 레벨은 브라우저 콘솔에만.
 *
 * 표준 API(`process.stdout` / `navigator.sendBeacon` / `JSON.stringify`)만 사용하므로
 * 서버·클라 동일 파일이 isomorphic 하게 동작한다. (node 전용 모듈 import 없음)
 */

export type Level = 'fatal' | 'error' | 'warn' | 'info' | 'debug' | 'trace';

const ORDER: Record<Level, number> = {
  fatal: 60,
  error: 50,
  warn: 40,
  info: 30,
  debug: 20,
  trace: 10,
};

/** 브라우저 콘솔 메서드 매핑 (console.fatal/trace 노이즈 회피) */
const CONSOLE_METHOD: Record<Level, 'error' | 'warn' | 'info' | 'debug'> = {
  fatal: 'error',
  error: 'error',
  warn: 'warn',
  info: 'info',
  debug: 'debug',
  trace: 'debug',
};

const IS_SERVER = typeof window === 'undefined';
const APP_NAME = process.env.NEXT_PUBLIC_APP_NAME ?? 'commerce-client';
const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION;
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH ?? '';

const THRESHOLD =
  ORDER[(process.env.NEXT_PUBLIC_LOG_LEVEL as Level) ?? 'info'] ?? ORDER.info;

/** Error / 순환참조를 안전하게 직렬화하기 위한 replacer 팩토리 */
function createReplacer(): (key: string, value: unknown) => unknown {
  const seen = new WeakSet<object>();
  return (_key, value) => {
    if (value instanceof Error) {
      return { name: value.name, message: value.message, stack: value.stack };
    }
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]';
      }
      seen.add(value);
    }
    return value;
  };
}

function stringify(record: object): string {
  return JSON.stringify(record, createReplacer());
}

function buildRecord(
  level: Level,
  message: string | undefined,
  ctx: object,
): object {
  const { severity, severityName } = toRfc5424Severity(level);
  return {
    severity,
    severityName,
    level,
    time: new Date().toISOString(),
    appName: APP_NAME,
    version: APP_VERSION,
    hostname: process.env.HOSTNAME,
    procid: typeof process !== 'undefined' ? process.pid : undefined,
    // 활성 OTel 스팬의 trace_id/span_id (서버에서 채워짐, 클라는 web SDK 있을 때만)
    ...activeTraceFields(),
    ...ctx,
    message,
  };
}

function emitServer(record: object): void {
  process.stdout.write(`${stringify(record)}\n`);
}

/**
 * 전송용 record 변환: message 를 PII 마스킹하고, ctx 의 `err`(Error)를 OTel
 * semconv `exception.*` + `error.fingerprint` 로 치환한다. 원본 err 는
 * 마스킹·truncate 를 우회하므로 전송 payload 에서는 제외
 * (콘솔 출력은 원본 record 그대로).
 */
function toTransmitRecord(record: object): object {
  const { err, exception, message } = record as {
    err?: unknown;
    exception?: unknown;
    message?: unknown;
  };
  const rest: Record<string, unknown> = { ...record };
  if (typeof message === 'string') {
    rest.message = maskPii(message);
  }
  if (exception || !(err instanceof Error)) {
    return rest;
  }
  delete rest.err;
  return { ...rest, ...exceptionFieldsFromError(err) };
}

function emitClient(level: Level, record: object): void {
  // eslint-disable-next-line no-console -- 클라이언트 로깅 채널
  console[CONSOLE_METHOD[level]](record);

  if (ORDER[level] < ORDER.error || typeof navigator === 'undefined') {
    return;
  }

  const payload = stringify({
    ...toTransmitRecord(record),
    ...appContextFields(),
    source: 'client',
    userAgent: navigator.userAgent,
    url: typeof location !== 'undefined' ? location.href : undefined,
  });
  const endpoint = `${BASE_PATH}/api/log`;

  if (typeof navigator.sendBeacon === 'function') {
    navigator.sendBeacon(endpoint, payload);
    return;
  }
  // 전송 실패가 unhandledrejection 핸들러를 다시 깨우는 루프 방지
  void fetch(endpoint, {
    method: 'POST',
    body: payload,
    keepalive: true,
    headers: { 'content-type': 'application/json' },
  }).catch(() => {});
}

function emit(level: Level, ctx: object, message?: string): void {
  if (ORDER[level] < THRESHOLD) {
    return;
  }
  // 로거는 어떤 경우에도 애플리케이션을 죽이면 안 됨
  try {
    const record = buildRecord(level, message, ctx);
    if (IS_SERVER) {
      emitServer(record);
    } else {
      emitClient(level, record);
    }
  } catch {
    // 직렬화/출력 실패는 조용히 무시
  }
}

/** 호출 형태: `logger.error({ ...ctx }, 'message')` 또는 `logger.error('message')` */
function normalize(
  arg: object | string,
  message?: string,
): [object, string | undefined] {
  if (typeof arg === 'string') {
    return [{}, arg];
  }
  return [arg, message];
}

export const logger = {
  fatal: (arg: object | string, message?: string) =>
    emit('fatal', ...normalize(arg, message)),
  error: (arg: object | string, message?: string) =>
    emit('error', ...normalize(arg, message)),
  warn: (arg: object | string, message?: string) =>
    emit('warn', ...normalize(arg, message)),
  info: (arg: object | string, message?: string) =>
    emit('info', ...normalize(arg, message)),
  debug: (arg: object | string, message?: string) =>
    emit('debug', ...normalize(arg, message)),
  trace: (arg: object | string, message?: string) =>
    emit('trace', ...normalize(arg, message)),
};

export type {
  DownloadEvent,
  DownloadPayloadBase,
  ProductCategory,
} from './events';
export { track } from './track';
export { registerGlobalErrorHandlers } from './global-handlers';
export { reloadOnChunkError } from './chunk-reload';
